import {graphql, useFragment} from 'react-relay'
import {NestedListItemLeadingVisual} from '@github-ui/nested-list-view/NestedListItemLeadingVisual'

import type {SubIssueStateIcon$key} from './__generated__/SubIssueStateIcon.graphql'
import {useIssueState} from '@github-ui/use-issue-state'
import {useFeatureFlags} from '@github-ui/react-core/use-feature-flag'
import {IssueState} from '@github-ui/use-issue-state/constants'
import styles from './SubIssueStateIcon.module.css'

const stateIconFragment = graphql`
  fragment SubIssueStateIcon on Issue {
    state
    stateReason
    issueDependenciesSummary {
      blockedBy
    }
  }
`

type SubIssueStateIconProps = {
  dataKey: SubIssueStateIcon$key
}

// This component is duplicated in packages/memex/src/client/components/item-state.tsx.
const OutlineBlockedIcon = () => (
  <svg
    aria-hidden="true"
    className={styles.blockedIcon}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.79164 1.13729C3.87955 1.04939 3.99877 1 4.12309 1L7.87691 1C8.00123 1 8.12045 1.04939 8.20836 1.13729L10.8627 3.79164C10.9506 3.87955 11 3.99877 11 4.12309V7.87691C11 8.00123 10.9506 8.12045 10.8627 8.20836L8.20836 10.8627C8.12045 10.9506 8.00123 11 7.87691 11H4.1231C3.99877 11 3.87955 10.9506 3.79164 10.8627L1.13729 8.20836C1.04939 8.12045 1 8.00123 1 7.87691L1 4.1231C1 3.99877 1.04939 3.87955 1.13729 3.79164L3.79164 1.13729ZM4.31726 1.9375L1.9375 4.31726L1.9375 7.68274L4.31726 10.0625H7.68274L10.0625 7.68274V4.31726L7.68274 1.9375L4.31726 1.9375Z"
      fill="#CF222E"
    />
    <path
      d="M3.79164 5.67178C3.79164 5.46467 3.95953 5.29678 4.16664 5.29678H7.82984C8.03692 5.29678 8.20484 5.46467 8.20484 5.67178V6.35012C8.20484 6.55723 8.03692 6.72512 7.82984 6.72512H4.16664C3.95953 6.72512 3.79164 6.55723 3.79164 6.35012V5.67178Z"
      fill="#CF222E"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.79164 1.13729C3.87955 1.04939 3.99877 1 4.12309 1L7.87691 1C8.00123 1 8.12045 1.04939 8.20836 1.13729L10.8627 3.79164C10.9506 3.87955 11 3.99877 11 4.12309V7.87691C11 8.00123 10.9506 8.12045 10.8627 8.20836L8.20836 10.8627C8.12045 10.9506 8.00123 11 7.87691 11H4.1231C3.99877 11 3.87955 10.9506 3.79164 10.8627L1.13729 8.20836C1.04939 8.12045 1 8.00123 1 7.87691L1 4.1231C1 3.99877 1.04939 3.87955 1.13729 3.79164L3.79164 1.13729ZM4.31726 1.9375L1.9375 4.31726L1.9375 7.68274L4.31726 10.0625H7.68274L10.0625 7.68274V4.31726L7.68274 1.9375L4.31726 1.9375Z"
      stroke="#CF222E"
      strokeWidth="0.45"
      strokeLinejoin="round"
    />
    <path
      d="M3.79164 5.67178C3.79164 5.46467 3.95953 5.29678 4.16664 5.29678H7.82984C8.03692 5.29678 8.20484 5.46467 8.20484 5.67178V6.35012C8.20484 6.55723 8.03692 6.72512 7.82984 6.72512H4.16664C3.95953 6.72512 3.79164 6.55723 3.79164 6.35012V5.67178Z"
      stroke="#CF222E"
      strokeWidth="0.45"
      strokeLinejoin="round"
    />
  </svg>
)

export function SubIssueStateIcon({dataKey}: SubIssueStateIconProps) {
  const {state, stateReason, issueDependenciesSummary} = useFragment(stateIconFragment, dataKey)
  const {sourceIcon} = useIssueState({state, stateReason})
  const {issue_dependencies} = useFeatureFlags()
  const SubIssueIcon = sourceIcon('Issue')

  if (issue_dependencies && state !== IssueState.CLOSED && issueDependenciesSummary?.blockedBy > 0) {
    const stateIconWithBlockedStatus = (
      <div className={styles.blockedIssueIconWrapper}>
        <OutlineBlockedIcon />
        <SubIssueIcon className={styles.issueIcon} />
      </div>
    )

    return (
      <NestedListItemLeadingVisual data-testid="nested-list-item-state-icon">
        {stateIconWithBlockedStatus}
      </NestedListItemLeadingVisual>
    )
  }

  return <NestedListItemLeadingVisual icon={SubIssueIcon} data-testid="nested-list-item-state-icon" />
}

try{ OutlineBlockedIcon.displayName ||= 'OutlineBlockedIcon' } catch {}
try{ SubIssueStateIcon.displayName ||= 'SubIssueStateIcon' } catch {}
try{ SubIssueIcon.displayName ||= 'SubIssueIcon' } catch {}